/**
 * The Action card's words and buttons (decision 165 as amended, specs/changes.md 2 and 4), from one change, for
 * the side reading it. Pure, so every row of the table is tested. The change's shape is the client's reading of
 * a liveChanges entry (booking-contracts.md 16, as UI/UX lands it); until then the lesson's open proposals fill
 * it (rows 1, 4, 9a).
 *
 * The count rule (165 as amended): one time, a singular title, one row and "Confirm Wednesday 11:50 AM"; two or
 * three, a plural title, the rows to pick from, "Pick a Time" (disabled) until one is picked.
 */
import { timeLabel, whenLine } from './liveCalendar.ts';

export type ChangeKind =
  | 'times'           // 1: the coach suggests times for one lesson
  | 'held_offer'      // 9a: the coach offers a new time after a no-show (row 1, its own title)
  | 'weekly'          // 2: the coach suggests a weekly time
  | 'asked'           // 4: the player sends other times ("None of these work", the mirror of 1)
  | 'moved'           // 3: the player moved a lesson to an open time (instant)
  | 'held_moved'      // 6: the player moved a held date (as 3)
  | 'weekly_changed'  // 5: the player changed the weekly time (instant)
  | 'match'           // 7: the player uploaded the match
  | 'joined'          // 8: either side is on the call
  | 'counted';        // 9: the coach counted a no-show

export interface LiveChange {
  id: string;
  kind: ChangeKind;
  /** Who made the change. */
  by: 'coach' | 'player';
  coachFirst: string;
  playerFirst: string;
  /** "Lesson 2"; a single lesson without a number reads "your lesson" on the player's side. */
  lesson?: string;
  /** The old time (ISO), or the old weekly time's words ("Wednesdays at 3:00 PM"). */
  from?: string;
  /** The new time or times (ISO), or weekly words ("Thursdays at 11:50 AM"); at most three. */
  to: string[];
  status: 'open' | 'answered' | 'passed' | 'withdrawn' | 'cleared';
  /** Answered: the one confirmed (an entry of `to`). */
  answer?: string;
  at: string;
  bookingId?: string;
  membershipId?: string;
  proposalId?: string;
  /** Row 7: the match's first frame, and its video for the length (the walk of 4 Nov, BOOKING 4). */
  match?: { thumbUrl?: string; seconds?: number; videoUrl?: string };
  /** Cleared another way (kept, replaced, the lesson moved since): the thread's line for it, no buttons. */
  result?: string;
  /** False where the server has no withdraw for it: the sender's card has no Withdraw. */
  canWithdraw?: boolean;
}

export type PrimaryAct = 'confirm' | 'join' | 'open' | 'looks_good';
export type SecondaryAct = 'none_work' | 'suggest_different' | 'withdraw' | 'message';

export interface ActionCardModel {
  title: string;
  /** 9a's line under the title, by count: "A new time for Lesson 3", "New times for Lesson 3". */
  subtitle?: string;
  /** The old thing, struck through. */
  struck?: string;
  /** The new thing in bold when there is one, else the rows to pick from. */
  bold?: string;
  rows: Array<{ value: string; label: string }>;
  /** The rows are choices: a tap selects one. */
  pick: boolean;
  /** A grey line under the body: "Waiting for Ana", "Ryan is told.", the counted line. */
  note?: string;
  primary?: { act: PrimaryAct; label: string; disabled: boolean };
  secondary?: { act: SecondaryAct; label: string };
  /** Flipped: a check, the result line, no buttons. */
  done?: boolean;
}

const WEEKLY: ChangeKind[] = ['weekly', 'weekly_changed'];

/** "Wed, Nov 4 at 11:50 AM", or a weekly time's own words. */
function label(kind: ChangeKind, v: string, tz: string): string {
  return WEEKLY.includes(kind) ? v : whenLine(v, tz);
}

/** "Confirm Wednesday 11:50 AM"; a weekly time "Confirm Thursdays 11:50 AM". */
export function confirmLabel(kind: ChangeKind, v: string, tz: string): string {
  if (WEEKLY.includes(kind)) return `Confirm ${v.replace(/ at /, ' ')}`;
  const d = new Date(v);
  const day = d.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
  return `Confirm ${day} ${time}`;
}

export function actionCardModel(c: LiveChange, me: 'coach' | 'player', tz: string, picked: string | null = null): ActionCardModel {
  const sender = c.by === me;
  const actor = c.by === 'coach' ? c.coachFirst : c.playerFirst;
  const other = me === 'coach' ? c.playerFirst : c.coachFirst;
  const who = sender ? 'You' : actor;
  const lesson = c.lesson ?? (me === 'player' ? 'your lesson' : 'the lesson');
  const one = c.to.length === 1;
  const struck = c.from ? label(c.kind, c.from, tz) : undefined;
  const rows = c.to.map((v) => ({ value: v, label: label(c.kind, v, tz) }));
  const asking = c.kind === 'times' || c.kind === 'held_offer' || c.kind === 'weekly' || c.kind === 'asked';

  // Answered, withdrawn or passed: the result, no buttons (the sender of a passed one may suggest again).
  if (c.status === 'answered' && asking) {
    const answerer = sender ? other : 'You';
    const v = c.answer ?? c.to[0];
    return { title: `${answerer} confirmed ${label(c.kind, v, tz)}`, rows: [], pick: false, done: true };
  }
  if (c.status === 'cleared' && asking) {
    return { title: c.result ?? `${c.lesson ?? 'The lesson'} stays ${struck ?? 'as it was'}.`, rows: [], pick: false };
  }
  if (c.status === 'withdrawn') {
    const weeklyNoun = c.kind === 'weekly' ? 'weekly ' : '';
    return { title: `${who} withdrew the suggested ${weeklyNoun}${one ? 'time' : 'times'}.`, rows: [], pick: false, done: true };
  }
  if (c.status === 'passed' && asking) {
    return {
      title: one ? 'This time has passed.' : 'These times have passed.',
      note: struck ? `${c.lesson ?? 'The lesson'} stays ${struck}.` : undefined,
      rows: [], pick: false,
      secondary: sender ? { act: 'suggest_different', label: 'Suggest a Different Time' } : undefined,
    };
  }

  // Rows 7, 8 and 9 once settled (joined, expired, or the thread's history): their line, no buttons.
  if (c.status !== 'open' && (c.kind === 'match' || c.kind === 'joined' || c.kind === 'counted')) {
    const m = actionCardModel({ ...c, status: 'open' }, me, tz);
    return { title: m.title, note: m.note, rows: [], pick: false };
  }

  switch (c.kind) {
    case 'times':
    case 'held_offer':
    case 'weekly':
    case 'asked': {
      const weekly = c.kind === 'weekly';
      const noun = weekly ? (one ? 'a new weekly time' : 'new weekly times') : (one ? 'a new time' : 'new times');
      // 9a's answerer reads Row 1 under its own title (165 as amended); every other side reads Row 1's title.
      const held = c.kind === 'held_offer' && !sender;
      const title = held ? `${actor} is holding ${c.lesson ?? 'your lesson'} for you`
        : weekly ? `${who} suggested ${noun}` : `${who} suggested ${noun} for ${lesson}`;
      const subtitle = held ? `${one ? 'A new time' : 'New times'} for ${c.lesson ?? 'your lesson'}` : undefined;
      if (sender) {
        return { title, subtitle, struck, rows, pick: false, note: `Waiting for ${other}`,
          secondary: c.canWithdraw === false ? undefined : { act: 'withdraw', label: 'Withdraw' } };
      }
      const chosen = one ? c.to[0] : picked;
      return {
        title, subtitle, struck, rows, pick: !one,
        primary: chosen ? { act: 'confirm', label: confirmLabel(c.kind, chosen, tz), disabled: false }
          : { act: 'confirm', label: 'Pick a Time', disabled: true },
        secondary: c.kind === 'times' || held
          ? { act: 'none_work', label: 'None of these work' }
          : { act: 'suggest_different', label: 'Suggest a Different Time' },
      };
    }
    case 'moved':
    case 'held_moved':
    case 'weekly_changed': {
      const weekly = c.kind === 'weekly_changed';
      const bold = c.to[0] ? label(c.kind, c.to[0], tz) : undefined;
      if (sender) {
        // Instant (123, 125, 89): the mover's card is already flipped.
        return { title: weekly ? 'You changed your weekly time' : `You moved ${c.lesson ?? 'your lesson'}`,
          struck, bold, rows: [], pick: false, note: `${other} is told.`, done: true };
      }
      if (c.status === 'cleared') {
        return { title: weekly ? `${actor} changed the weekly time` : `${actor} moved ${lesson}`, struck, bold, rows: [], pick: false, done: true };
      }
      return {
        title: weekly ? `${actor} changed the weekly time` : `${actor} moved ${lesson} to ${bold ?? ''}`.trim(),
        struck, bold, rows: [], pick: false,
        primary: { act: 'looks_good', label: 'Looks Good', disabled: false },
        secondary: { act: 'suggest_different', label: 'Suggest a Different Time' },
      };
    }
    case 'match':
      return sender
        ? { title: 'You uploaded your match', rows: [], pick: false, done: true }
        : { title: `${actor} uploaded the match`, rows: [], pick: false,
          primary: { act: 'open', label: 'Open Lesson', disabled: false } };
    case 'joined':
      return { title: `${actor} is on the call`, rows: [], pick: false,
        primary: { act: 'join', label: 'Join', disabled: false } };
    case 'counted':
      return sender
        ? { title: `You counted ${lesson}`, rows: [], pick: false, done: true }
        : { title: `${actor} counted ${c.lesson ?? 'your lesson'}`, rows: [], pick: false,
          note: struck ? `You missed ${c.lesson ?? 'your lesson'} on ${struck}. One lesson credit was used.` : undefined,
          primary: { act: 'open', label: 'Open Lesson', disabled: false },
          secondary: { act: 'message', label: `Message ${actor}` } };
  }
}

/** Several open: newest first, at most three on a page, then "2 more" (165 as amended). */
export function stackCards<T extends { at: string }>(changes: T[], max = 3): { shown: T[]; more: number } {
  const sorted = [...changes].sort((a, b) => b.at.localeCompare(a.at));
  return { shown: sorted.slice(0, max), more: Math.max(0, sorted.length - max) };
}

/**
 * Until booking-contracts.md 16's liveChanges lands: a lesson's time proposal (160) as its change, row 1 when
 * the coach sent it (9a on a lesson held after a no-show) and row 4 when the player did. Passed by the server's
 * job or the client's clock.
 */
export function changeFromProposal(
  p: { id: string; bookingId: string; membershipId?: string; proposedBy?: 'coach' | 'player'; status: string;
    fromStartAt: string; lesson?: string; createdAt?: string; times: Array<{ startAt: string; expiresAt?: string }> },
  names: { coachFirst: string; playerFirst: string },
  nowMs: number,
  /** The lesson is held after a no-show and the coach offered times (missResolution "offered", 154): row 9a. */
  heldOffer = false,
): LiveChange {
  const by = p.proposedBy ?? 'coach';
  const now = new Date(nowMs).toISOString();
  const passed = p.status === 'passed' || (p.times.length > 0 && p.times.every((t) => (t.expiresAt ?? t.startAt) <= now));
  return {
    id: `proposal:${p.id}`, kind: by === 'coach' ? (heldOffer ? 'held_offer' : 'times') : 'asked', by, ...names,
    lesson: p.lesson, from: p.fromStartAt, to: p.times.map((t) => t.startAt).slice(0, 3),
    status: passed ? 'passed' : 'open', at: p.createdAt ?? p.fromStartAt,
    bookingId: p.bookingId, membershipId: p.membershipId, proposalId: p.id,
  };
}

/**
 * A proposal's thread card (live_lesson_time_proposal, contracts 6 and 15) as its change: open until answered or
 * passed, accepted as answered, and kept, replaced or moved since as a plain line (`result`).
 */
export function changeFromThreadProposal(
  sent: { proposalId: string; bookingId: string; lesson: string; fromStartAt: string; proposedBy?: 'coach' | 'player';
    status: string; chosen?: number; times: Array<{ startAt: string; expiresAt?: string }> },
  names: { coachFirst: string; playerFirst: string },
  at: string,
  nowMs: number,
  result?: string | null,
): LiveChange {
  const c = changeFromProposal({ id: sent.proposalId, bookingId: sent.bookingId, proposedBy: sent.proposedBy, status: 'open',
    fromStartAt: sent.fromStartAt, lesson: sent.lesson, createdAt: at, times: sent.times }, names, nowMs);
  if (sent.status === 'open') return c;
  if (sent.status === 'accepted') return { ...c, status: 'answered', answer: sent.times[sent.chosen ?? 0]?.startAt };
  return { ...c, status: 'cleared', result: result ?? undefined };
}

/** The server's "Every Thursday at 11:50 AM" as the card's weekly words, "Thursdays at 11:50 AM" (changes.md 4, row 2). */
export function weeklyWords(line: string): string {
  const m = /^Every (\w+day) at (.+)$/.exec(line);
  return m ? `${m[1]}s at ${m[2]}` : line;
}

/**
 * A weekly suggestion's thread card (live_weekly_proposal, decision 96) as its change, row 2: the slots as weekly
 * words in the reader's zone, the old weekly time struck through, accepted as answered, kept, picking or replaced
 * as a plain line. The coach's card withdraws it (withdrawLiveWeeklyTimeProposal).
 */
export function changeFromWeeklyProposal(
  p: { proposalId: string; membershipId: string; status: string; chosen?: number; slotLabels: string[] },
  names: { coachFirst: string; playerFirst: string },
  at: string,
  from?: string,
  result?: string | null,
): LiveChange {
  const to = p.slotLabels.map(weeklyWords).slice(0, 3);
  const c: LiveChange = { id: `weekly:${p.proposalId}`, kind: 'weekly', by: 'coach', ...names, to, at,
    from: from ? weeklyWords(from) : undefined, status: 'open', membershipId: p.membershipId, proposalId: p.proposalId };
  if (p.status === 'open') return c;
  if (p.status === 'accepted') return { ...c, status: 'answered', answer: to[p.chosen ?? 0] };
  if (p.status === 'withdrawn') return { ...c, status: 'withdrawn' };
  return { ...c, status: 'cleared', result: result ?? undefined };
}

/** A liveChanges record (booking-contracts.md 16), as the server writes it. */
export interface LiveChangeRecord {
  id: string;
  kind: 'times_proposed' | 'weekly_proposed' | 'lesson_moved' | 'times_requested' | 'weekly_changed' | 'match_uploaded' | 'joined' | 'counted';
  coachId: string; playerId: string;
  actor: 'coach' | 'player' | 'auto';
  bookingId?: string | null; holdId?: string | null; membershipId?: string | null; proposalId?: string | null;
  lesson?: string | null;
  old?: unknown; new?: unknown;
  times?: string[]; count?: number;
  state: 'open' | 'answered' | 'withdrawn' | 'expired';
  answer?: 'confirmed' | 'none_work' | 'kept' | 'looks_good' | 'suggested' | 'joined' | null;
  answeredBy?: string | null; answeredAt?: string | null;
  expiresAt?: string | null;
  reason?: string;
  createdAt: string;
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** A weekly start as the card's words in the reader's zone: "Thursdays at 11:50 AM". */
export function weeklyWordsAt(startAt: string, tz: string): string {
  const day = new Date(startAt).toLocaleDateString('en-US', { weekday: 'long', timeZone: tz });
  return `${WEEKDAY_NAMES.includes(day) ? day : 'Week'}s at ${timeLabel(startAt, tz)}`;
}

const isoOf = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const weeklyOf = (v: unknown, tz: string): string | undefined => {
  const w = v as { startAt?: string } | null | undefined;
  return w?.startAt ? weeklyWordsAt(w.startAt, tz) : undefined;
};

/**
 * A liveChanges record as the card's change (contracts 16), for the reader's zone: the server's kinds onto the
 * table's rows, its state and answer onto the card's result.
 */
export function changeFromRecord(r: LiveChangeRecord, names: { coachFirst: string; playerFirst: string }, tz: string): LiveChange {
  const kind: ChangeKind = r.kind === 'times_proposed' ? (r.reason === 'no_show_offer' ? 'held_offer' : 'times')
    : r.kind === 'weekly_proposed' ? 'weekly'
      : r.kind === 'lesson_moved' ? (r.holdId ? 'held_moved' : 'moved')
        : r.kind === 'times_requested' ? 'asked'
          : r.kind === 'weekly_changed' ? 'weekly_changed'
            : r.kind === 'match_uploaded' ? 'match'
              : r.kind === 'joined' ? 'joined' : 'counted';
  const weekly = kind === 'weekly' || kind === 'weekly_changed';
  const word = (v: unknown) => (weekly ? weeklyOf(v, tz) : isoOf(v));
  const to = kind === 'weekly' ? (r.times ?? []).map((t) => weeklyWordsAt(t, tz))
    : kind === 'times' || kind === 'held_offer' || kind === 'asked' ? (r.times ?? [])
      : kind === 'moved' || kind === 'held_moved' || kind === 'weekly_changed' ? [word(r.new)].filter((x): x is string => !!x)
        : [];
  const by: 'coach' | 'player' = r.actor === 'player' ? 'player' : 'coach';
  const base: LiveChange = { id: r.id, kind, by, ...names, lesson: r.lesson ?? undefined, from: word(r.old), to: to.slice(0, 3),
    // An answer is news when it's answered: it sorts by answeredAt (the walk of 4 Nov, BOOKING 6).
    status: 'open', at: r.state === 'answered' && r.answeredAt ? r.answeredAt : r.createdAt, bookingId: r.bookingId ?? undefined, membershipId: r.membershipId ?? undefined,
    proposalId: r.proposalId ?? undefined,
    ...(kind === 'match' && { match: { thumbUrl: (r.new as { thumbnailUrl?: string } | null)?.thumbnailUrl ?? undefined } }) };
  if (r.state === 'open') return base;
  if (r.state === 'withdrawn') return { ...base, status: 'withdrawn' };
  if (r.state === 'expired') return { ...base, status: 'passed' };
  if (r.answer === 'confirmed') {
    const chosen = weekly ? weeklyOf(r.new, tz) : isoOf(r.new);
    return { ...base, status: 'answered', answer: chosen ?? base.to[0] };
  }
  if (r.answer === 'none_work') {
    const asker = by === 'coach' ? names.playerFirst : names.coachFirst;
    return { ...base, status: 'cleared', result: `${asker} asked for other times.` };
  }
  return { ...base, status: 'cleared' };
}

/** The thread lines that are an Action card at the moment they happened (rows 3, 5, 6, 7, 8, 9; contracts 16). */
export const CHANGE_LINE_EVENTS = new Set(['lesson_moved', 'weekly_time_changed', 'match_uploaded', 'joined_call', 'lesson_counted']);

/**
 * Which of a page's changes it shows (165 as briefed, 4 Nov evening): "Ryan is on the call" (row 8) is Lessons'
 * only; the lesson page's one Join is its state card's, and a plan page has the lesson's row.
 */
export function forPage<T extends { kind: ChangeKind }>(changes: T[], page: 'lessons' | 'plan' | 'lesson'): T[] {
  return page === 'lessons' ? changes : changes.filter((c) => c.kind !== 'joined');
}

/**
 * One card per lesson for the one-shot kinds (the walk of 4 Nov, BOOKING 3): an upload, a join or a count can
 * carry two records (a match attached and then finished); the newest stands for both.
 */
export function newestPerLesson<T extends { kind: ChangeKind; bookingId?: string; at: string }>(changes: T[]): T[] {
  const ONE_SHOT: ChangeKind[] = ['match', 'joined', 'counted'];
  const newest = new Map<string, T>();
  for (const c of changes) {
    if (!ONE_SHOT.includes(c.kind) || !c.bookingId) continue;
    const k = `${c.kind}:${c.bookingId}`;
    const was = newest.get(k);
    if (!was || c.at > was.at) newest.set(k, c);
  }
  return changes.filter((c) => !ONE_SHOT.includes(c.kind) || !c.bookingId || newest.get(`${c.kind}:${c.bookingId}`) === c);
}

/** A match's length as the card reads it: "1:42", or "1:02:05" past the hour. */
export function matchLength(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

/**
 * The records a side's pages read: every open one, and those the other side answered in the last three days, so
 * the sender sees "Ana confirmed ..." once (the walk of 4 Nov, BOOKING 6). This side's own answers it saw flip.
 */
export function recentAnswersOf<T extends Pick<LiveChangeRecord, 'state' | 'answeredBy' | 'answeredAt'>>(records: T[], uid: string, nowMs: number): T[] {
  return records.filter((r) => r.state === 'open' || (r.state === 'answered' && !!r.answeredBy && r.answeredBy !== uid
    && !!r.answeredAt && nowMs - Date.parse(r.answeredAt) < 3 * 86_400_000));
}

const isoOf2 = (v: unknown): number => {
  if (typeof v === 'string') return Date.parse(v);
  const t = v as { toMillis?: () => number } | null | undefined;
  return typeof t?.toMillis === 'function' ? t.toMillis() : NaN;
};

/**
 * The other side on the call from the lesson itself (the walk of 4 Nov, BOOKING 9): joinLiveLesson stamps
 * `<side>JoinedAt` and `<side>SeenAt` on the booking in its first write, before its change record, so the card
 * shows as soon as the lesson's own update arrives. In: joined, seen inside two minutes, not left since.
 */
export function otherOnCall(b: Record<string, unknown>, me: 'coach' | 'player', nowMs: number): boolean {
  const other = me === 'coach' ? 'player' : 'coach';
  const joined = isoOf2(b[`${other}JoinedAt`]);
  const seen = isoOf2(b[`${other}SeenAt`]);
  const left = isoOf2(b[`${other}LeftAt`]);
  if (!Number.isFinite(joined) || !Number.isFinite(seen)) return false;
  if (nowMs - seen > 120_000) return false;
  return !(Number.isFinite(left) && left >= seen);
}

/**
 * The records a page draws (the re-walk of 4 Nov, BOOKING 1, 2 and 7): never this side's own join ("Ryan is on
 * the call" is the other side's, with Join); never an open record past its expiry (a call long over); and this
 * side's own results (a move, a weekly change, an upload, a count it made) only within 15 minutes, since the
 * device that made them showed them at once. Until liveChanges carries a seenAt.
 */
export function visibleRecords<T extends Pick<LiveChangeRecord, 'kind' | 'actor' | 'state' | 'expiresAt' | 'createdAt'>>(records: T[], me: 'coach' | 'player', nowMs: number): T[] {
  const mine = (r: T) => (r.actor === 'auto' ? 'coach' : r.actor) === me;
  const OWN_RESULTS: LiveChangeRecord['kind'][] = ['lesson_moved', 'weekly_changed', 'match_uploaded', 'counted'];
  return records.filter((r) => {
    if (r.kind === 'joined' && mine(r)) return false;
    if (r.state === 'open' && r.expiresAt && Date.parse(r.expiresAt) <= nowMs) return false;
    if (OWN_RESULTS.includes(r.kind) && mine(r) && nowMs - Date.parse(r.createdAt) > 15 * 60_000) return false;
    return true;
  });
}
